import { Github, Linkedin, Twitter, Rss, Globe, ArrowRight, BookOpen } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { useI18n } from "@/i18n/I18nProvider";

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com/", testid: "about-linkedin" },
  { icon: Twitter, label: "X / Twitter", href: "https://twitter.com/", testid: "about-twitter" },
  { icon: Rss, label: "Bluesky", href: "https://bsky.app/", testid: "about-bluesky" },
  { icon: Globe, label: "Reddit", href: "https://reddit.com/", testid: "about-reddit" },
  { icon: Github, label: "GitHub", href: "https://github.com/", testid: "about-github" },
];

export default function AboutPage() {
  const { t } = useI18n();
  const areas = t("about.experience.areas") || [];
  const approach = t("about.approach.items") || [];

  return (
    <>
      <PageHero
        eyebrow={t("about.hero.eyebrow")}
        title={t("about.hero.title")}
        description={t("about.hero.description")}
      />

      {/* STORY */}
      <section data-testid="about-story" className="bg-brand-off py-20 md:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">{t("about.story.eyebrow")}</p>
            <h2 className="text-3xl md:text-[40px] font-light tracking-tight leading-[1.1] text-brand-charcoal">
              {t("about.story.title")}
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <p className="text-[18px] leading-[1.75] text-brand-charcoal/90">{t("about.story.paragraph1")}</p>
            <p className="mt-5 text-[16.5px] leading-[1.75] text-brand-muted">{t("about.story.paragraph2")}</p>
            <p className="mt-5 text-[16.5px] leading-[1.75] text-brand-muted">{t("about.story.paragraph3")}</p>
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section data-testid="about-experience" className="bg-brand-paper py-20 md:py-28 border-t border-brand-line">
        <div className="container-x">
          <SectionHeading
            eyebrow={t("about.experience.eyebrow")}
            title={t("about.experience.title")}
            className="mb-12"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {areas.map((a, i) => (
              <article
                key={i}
                data-testid={`about-area-${i + 1}`}
                className="rounded-2xl bg-white border border-black/[0.06] p-8 soft-shadow hover-lift soft-shadow-hover"
              >
                <span className="text-[11px] tracking-[0.2em] uppercase text-brand-muted">{a.tag}</span>
                <h3 className="mt-4 text-[24px] font-light tracking-tight text-brand-charcoal leading-tight">
                  {a.title}
                </h3>
                <p className="mt-4 text-[15.5px] leading-relaxed text-brand-muted">{a.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section data-testid="about-approach" className="bg-brand-off py-20 md:py-28">
        <div className="container-x">
          <SectionHeading
            eyebrow={t("about.approach.eyebrow")}
            title={t("about.approach.title")}
            className="mb-12"
          />
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {approach.map((a, idx) => (
              <li
                key={idx}
                data-testid={`approach-${idx + 1}`}
                className="rounded-2xl bg-white border border-black/[0.06] p-7 soft-shadow hover-lift soft-shadow-hover"
              >
                <span className="text-[11px] tracking-[0.2em] uppercase text-brand-muted">
                  0{idx + 1}
                </span>
                <p className="mt-3 text-[19px] leading-snug text-brand-charcoal font-medium">{a}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CLARITY */}
      <section data-testid="about-clarity" className="bg-brand-charcoal text-brand-off py-20 md:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow-light mb-4">{t("about.clarity.eyebrow")}</p>
            <h2 className="text-3xl md:text-[40px] font-light tracking-tight leading-[1.1] text-brand-off">
              {t("about.clarity.title")}
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <p className="text-[17px] leading-[1.75] text-white/80">{t("about.clarity.paragraph1")}</p>
            <p className="mt-5 text-[16px] leading-[1.75] text-white/65">{t("about.clarity.paragraph2")}</p>
          </div>
        </div>
      </section>

      {/* BOOK */}
      <section data-testid="about-book" className="bg-brand-off py-20 md:py-28">
        <div className="container-x">
          <div className="rounded-2xl bg-white border border-black/[0.06] p-8 md:p-12 soft-shadow grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-4 flex items-start gap-4">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-paper border border-brand-line shrink-0">
                <BookOpen className="w-5 h-5 text-brand-charcoal" strokeWidth={1.4} />
              </span>
              <div>
                <p className="eyebrow mb-3">{t("about.book.eyebrow")}</p>
                <h3 className="text-2xl md:text-[28px] font-light tracking-tight text-brand-charcoal leading-tight">
                  {t("about.book.title")}
                </h3>
              </div>
            </div>
            <div className="lg:col-span-8 lg:pt-2">
              <p className="text-[16px] leading-[1.75] text-brand-charcoal/90">
                {t("about.book.description")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL */}
      <section data-testid="about-social" className="bg-brand-paper py-20 md:py-28 border-t border-brand-line">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <SectionHeading eyebrow={t("about.social.eyebrow")} title={t("about.social.title")} />
          </div>
          <div className="lg:col-span-6">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SOCIALS.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={s.testid}
                    className="group flex items-center justify-between gap-4 rounded-2xl bg-white border border-black/[0.06] px-5 py-4 hover-lift soft-shadow soft-shadow-hover"
                  >
                    <span className="flex items-center gap-3 text-[15px] text-brand-charcoal font-medium">
                      <s.icon className="w-4 h-4 text-brand-blue" strokeWidth={1.5} />
                      {s.label}
                    </span>
                    <ArrowRight className="w-4 h-4 text-brand-muted transition-transform group-hover:translate-x-1" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
